import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import {
  Alert,
  ConfigProvider,
  Flex,
  Form,
  FormInstance,
  FormProps,
  GlobalToken,
  Modal,
  notification,
  NotificationArgsProps,
  Space,
} from 'antd';
import { useToken } from 'antd/es/theme/internal';
import { duration } from 'moment';
import React, { ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import LoadingOverlay from 'src/common/loading-overlay';
import { createLoadingState, LoadingProvider } from 'src/common/use-loading';
import { isValidateErrors } from 'src/common/validation';
import HeightAnimator from 'src/components/height-animator';
import { Progress } from 'src/components/progress';
import getAntdConfigProps from 'src/vendor/antd/antd-config';

import { ProgressPromise, UpdateProgressFunc } from '../progress-promise';

export type ModalIcon = 'info' | 'warn' | 'success' | 'error';

type ShowModalAntdProps = {
  width?: number;
  icon?: ModalIcon | ReactNode;
};

export type ShowAlertAntdProps = ShowModalAntdProps & {
  okButtonText?: string;
  okType?: 'default' | 'primary' | 'dashed' | 'link' | 'text' | 'danger';
  extraFooter?: ReactNode;
  extraButton?: (closeModal: () => void) => ReactElement;
};

export type ShowConfirmAntdProps = ShowAlertAntdProps & {
  onConfirm?: (updateFunc: UpdateProgressFunc) => Promise<void | ProgressPromise | false> | ProgressPromise | false;
  onBeforeCancel?: () => Promise<void>;
  cancelButtonText?: string;
  showCancel?: boolean;
  extraButton?: (closeModal: (result: boolean) => void) => ReactElement;
};

export type ShowProgressAntdProps = ShowModalAntdProps & {
  defaultError?: string;
};

export type ShowFormAntdProps<TForm> = Omit<ShowConfirmAntdProps, 'onConfirm'> & {
  onConfirm?: (
    values: TForm,
    form: FormInstance<TForm>,
    updateFunc: UpdateProgressFunc,
  ) => Promise<void | ProgressPromise | false> | ProgressPromise | false;
  formProps?: Exclude<FormProps<TForm>, 'form'>;
};

interface ConfirmModalInstance {
  setOkButtonDisabled: (disabled: boolean) => void;
  setExtraButtonDisabled: (disabled: boolean) => void;
}

const ConfirmModalContext = React.createContext<ConfirmModalInstance>({
  setOkButtonDisabled: () => undefined,
  setExtraButtonDisabled: () => undefined,
});

export function useConfirmModalContext() {
  return useContext(ConfirmModalContext);
}

export type AlertModalProps = React.PropsWithChildren<
  ShowAlertAntdProps & {
    onClose: () => void;
    open: boolean;
    testId: string;
    title: string;
  }
>;

export type ConfirmModalProps = React.PropsWithChildren<
  ShowConfirmAntdProps & {
    onClose: (result: boolean) => void;
    open: boolean;
    testId: string;
    title: string;
  }
>;

export type ProgressModalProps = React.PropsWithChildren<
  ShowProgressAntdProps & {
    promise: PromiseLike<ProgressPromise | void> | ProgressPromise;
    onClose: (result: boolean) => void;
    open: boolean;
    testId: string;
    title: string;
  }
>;

function modalIcon(token: GlobalToken, icon?: ModalIcon | ReactNode) {
  switch (icon) {
    case 'error':
      return <CloseCircleFilled style={{ color: token.colorError }} />;
    case 'info':
      return <InfoCircleFilled style={{ color: token.colorInfo }} />;
    case 'success':
      return <CheckCircleFilled style={{ color: token.colorSuccess }} />;
    case 'warn':
      return <ExclamationCircleFilled style={{ color: token.colorWarning }} />;
    default:
      return icon;
  }
}

function titleFunc(title: ReactNode, icon?: ModalIcon | ReactNode) {
  const [_, token] = useToken();
  return icon ? (
    <Space>
      {modalIcon(token, icon)}
      {title}
    </Space>
  ) : (
    title
  );
}

function footerFunc(extraFooter?: ReactNode, extraButton?: ReactElement, extraButtonDisabled?: boolean, showCancel = true) {
  return (_: ReactNode, { OkBtn, CancelBtn }: { OkBtn: React.FC; CancelBtn: React.FC }) => {
    return extraFooter ? (
      <Flex style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        {extraFooter}
        <Space>
          {extraButton}
          <OkBtn />
          {showCancel && <CancelBtn />}
        </Space>
      </Flex>
    ) : (
      <>
        {extraButton && React.cloneElement(extraButton, { disabled: extraButtonDisabled })}
        <OkBtn />
        {showCancel && <CancelBtn />}
      </>
    );
  };
}

export function AlertModal(props: AlertModalProps) {
  const { children, extraFooter, extraButton, icon, okButtonText, onClose, open, testId, title, ...modalProps } = props;
  const [loading, message, percent, setLoading, setMessage, setPercent] = createLoadingState();

  return (
    <LoadingProvider loadingState={[loading, message, percent, setLoading, setMessage, setPercent]}>
      <Modal
        mask={{ closable: false, blur: true }}
        okText={okButtonText || UiText.t('Shared_OK_Button')}
        centered
        destroyOnHidden
        onOk={onClose}
        onCancel={onClose}
        closable={!loading}
        {...modalProps}
        okButtonProps={{ disabled: loading }}
        open={open}
        footer={footerFunc(extraFooter, extraButton?.(onClose), false)}
        data-testid={testId}
        title={titleFunc(title, icon)}
      >
        <LoadingOverlay loading={loading} message={message} percent={percent}>
          <HeightAnimator>{children}</HeightAnimator>
        </LoadingOverlay>
      </Modal>
    </LoadingProvider>
  );
}

export function ConfirmModal(props: ConfirmModalProps) {
  const {
    cancelButtonText,
    children,
    extraFooter,
    extraButton,
    icon,
    okButtonText,
    onBeforeCancel,
    onClose,
    onConfirm,
    open,
    showCancel,
    testId,
    title,
    ...modalProps
  } = props;
  const [loading, message, percent, setLoading, setMessage, setPercent] = createLoadingState();
  const [okLoading, setOkLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(false);
  const [extraButtonDisabled, setExtraButtonDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setProgressMessage('');
      setProgressPercent(undefined);
      setErrorMessage('');
    }
  }, [props.open]);
  return (
    <LoadingProvider loadingState={[loading, message, percent, setLoading, setMessage, setPercent]}>
      <ConfirmModalContext.Provider value={{ setOkButtonDisabled, setExtraButtonDisabled }}>
        <Modal
          data-testid={testId}
          mask={{ closable: false, blur: true }}
          okText={okButtonText || UiText.t('Shared_OK_Button')}
          cancelText={cancelButtonText || UiText.t('Shared_Cancel_Button')}
          centered
          closable={!loading}
          onOk={() =>
            handleOk(
              onConfirm,
              () => onClose(true),
              (l) => {
                setLoading(l);
                setOkLoading(l);
              },
              setProgressMessage,
              setProgressPercent,
              setErrorMessage,
            )
          }
          onCancel={async () => {
            await onBeforeCancel?.();
            onClose(false);
          }}
          open={open}
          confirmLoading={okLoading}
          cancelButtonProps={{ disabled: loading }}
          footer={footerFunc(extraFooter, extraButton?.(onClose), extraButtonDisabled, showCancel)}
          title={titleFunc(title, icon)}
          okButtonProps={{ disabled: okButtonDisabled || loading }}
          {...modalProps}
        >
          <LoadingOverlay loading={loading} message={progressMessage || message} percent={progressPercent ?? percent}>
            <HeightAnimator>
              <Flex vertical gap="middle">
                {children}
                {errorMessage && <Alert showIcon type="error" message={errorMessage} />}
              </Flex>
            </HeightAnimator>
          </LoadingOverlay>
        </Modal>
      </ConfirmModalContext.Provider>
    </LoadingProvider>
  );
}

export async function showAlertAntd(testId: string, title: string, content: ReactNode, props?: ShowAlertAntdProps): Promise<void> {
  return showModalAntd<void>((open, onClose) => (
    <AlertModal onClose={onClose} open={open} testId={testId} title={title} {...props}>
      {content}
    </AlertModal>
  ));
}

export async function showConfirmAntd(
  testId: string,
  title: string,
  content: ReactNode,
  props?: ShowConfirmAntdProps,
): Promise<boolean | object> {
  return showModalAntd<boolean | object>((open, onClose) => (
    <ConfirmModal onClose={onClose} open={open} testId={testId} title={title} {...props}>
      {content}
    </ConfirmModal>
  ));
}

/**
 * Shows an antd modal with the content wrapped in a form. Items can be added directly. The form is passed into the `onConfirm()` callback.
 * The form can also be access by the child component using `Form.useFormInstance()`.
 */
export async function showFormModalAntd<TForm>(testId: string, title: string, content: ReactNode, props?: ShowFormAntdProps<TForm>) {
  let formInstance: FormInstance<TForm>;
  return await showConfirmAntd(testId, title, <Content formProps={props?.formProps}>{content}</Content>, {
    ...props,
    onConfirm:
      props?.onConfirm &&
      (async (updateFunc: UpdateProgressFunc) => await props.onConfirm!(await formInstance.validateFields(), formInstance, updateFunc)),
  });

  function Content({ children, formProps }: { readonly children?: ReactNode; readonly formProps?: Exclude<FormProps<TForm>, 'form'> }) {
    const [form] = Form.useForm<TForm>();
    useEffect(() => void (formInstance = form), []);
    return (
      <Form<TForm> form={form} {...formProps}>
        {children}
      </Form>
    );
  }
}

function ProgressModal(props: ProgressModalProps) {
  const [error, setError] = useState('');
  const [percent, setPercent] = useState<number | undefined>(undefined);

  useEffect(() => void waitForPromise(), []);

  return (
    <Modal
      mask={{ closable: false, blur: true }}
      centered
      open={props.open}
      onCancel={() => props.onClose(false)}
      onOk={() => props.onClose(false)}
      data-testid={props.testId}
      title={titleFunc(error || props.title, error ? 'error' : undefined)}
      closable={!!error}
      okText={UiText.t('Shared_DialogClose_Button')}
      footer={(_, { OkBtn }) => (error ? <OkBtn /> : false)}
    >
      <HeightAnimator>{error ? <></> : <Progress percent={percent} />}</HeightAnimator>
    </Modal>
  );

  async function waitForPromise() {
    try {
      const maybeProgress = await props.promise;
      if (maybeProgress) {
        await maybeProgress((_, percent) => setPercent(percent));
      }
      props.onClose(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(props.defaultError || UiText.t('Shared_UnknownErrorOccured_Error'));
      }
    }
  }
}

export function showProgressAntd(
  testId: string,
  title: string,
  promise: PromiseLike<ProgressPromise | void> | ProgressPromise,
  props?: ShowProgressAntdProps,
) {
  return showModalAntd<boolean>((open, onClose) => (
    <ProgressModal open={open} onClose={onClose} promise={promise} testId={testId} title={title} {...props} />
  ));
}

export async function showModalAntd<TResult>(modal: (open: boolean, onClose: (result: TResult) => void) => ReactNode): Promise<TResult> {
  const config = await getAntdConfigProps();
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.append(container);
    const reactRoot = createRoot(container);
    reactRoot.render(<ShowModal />);

    function ShowModal() {
      const [_, token] = useToken();
      const [open, setOpen] = useState(true);
      function onClose(result: TResult) {
        setOpen(false);
        setTimeout(
          () => {
            reactRoot.unmount();
            container.remove();
            // Need to wait for the closing animation before unmounting - this is the easiest way to parse the CSS time into milliseconds.
          },
          duration(`PT${token.motionDurationMid.toUpperCase()}`).asMilliseconds(),
        );
        resolve(result);
      }
      return <ConfigProvider {...config}>{modal(open, (result) => onClose(result))}</ConfigProvider>;
    }
  });
}

async function showNotificationAntD(title: string, content: string, type: 'error' | 'warning' | 'success' | 'info'): Promise<void> {
  const config = await getAntdConfigProps();
  const container = document.createElement('div');
  document.body.append(container);
  const reactRoot = createRoot(container);
  reactRoot.render(
    <ConfigProvider {...config}>
      <NotificationPopup title={title} content={content} type={type} />
    </ConfigProvider>,
  );
}

export interface NotificationPopupProps {
  title: string;
  content: string;
  type: 'error' | 'warning' | 'success' | 'info';
}

function NotificationPopup(props: Readonly<NotificationPopupProps>) {
  const [api, contextHolder] = notification.useNotification();
  const sharedProps: NotificationArgsProps = {
    title: props.title,
    description: props.content,
    duration: false,
  };

  useEffect(() => {
    if (props.type == 'error')
      api.error({
        ...sharedProps,
        type: 'error',
        placement: 'top',
        description: props.content,
      });
    else {
      api.info({
        ...sharedProps,
        placement: 'top',
        description: props.content,
      });
    }
  }, []);

  return <div data-testid={'antDNotificationPopup'}>{contextHolder}</div>;
}

export default showNotificationAntD;

async function handleOk(
  confirm: ((updateFunc: UpdateProgressFunc) => Promise<void | ProgressPromise | false> | ProgressPromise | false) | undefined,
  resolve: () => void,
  setLoading: (loading: boolean) => void,
  setProgressMessage: (message: string) => void,
  setProgressPercent: (percent?: number) => void,
  setErrorMessage: (error: string) => void,
) {
  setLoading(true);
  try {
    const updateFunc = (message: string, percent?: number) => {
      setProgressMessage(message);
      setProgressPercent(percent);
    };
    const result = await confirm?.(updateFunc);
    if (result) {
      await result(updateFunc);
      resolve();
    } else if (result === undefined) {
      resolve();
    }
  } catch (err) {
    // We expect validation errors from forms to be handled by the form itself.
    if (!isValidateErrors(err)) {
      setErrorMessage(err instanceof Error ? `${err.name}: ${err.message}` : UiText.t('Shared_UnknownErrorOccured_Error'));
      console.error(err);
    }
  } finally {
    setLoading(false);
  }
}
